import { Add, Edit } from "@mui/icons-material";
import BlindsClosedIcon from "@mui/icons-material/BlindsClosed";
import {
  Grid,
  IconButton,
  Skeleton,
  Stack,
  TableCell,
  Tooltip,
  Typography,
} from "@mui/material";
import EmptyContent from "components/EmptyContent";
import List from "components/UI/List";
import { useAlert } from "contexts/alertContext";
import { Feature } from "models/api";
import { IUser, IUserInput } from "models/users";
import React, { useCallback, useEffect, useState } from "react";
import { QueryClient, useMutation } from "react-query";

import { UserCells, addUser, editUser, getUsers } from "framework/users";
import Search from "components/Search/Search";
import AddUser from "./partials/AddUser/AddUser";
import EditUser from "./partials/EditUser/EditUser";
import { handleError, isValidEmail } from "helpers/utils";
import { useTranslation } from "react-i18next";
import { IPermission } from "models/permissions";
import { getPermission, getUserData } from "helpers";
import { IRole } from "models/Roles";
import { getRoles, getRolesDropDown } from "framework/roles";
import phone from "phone";
import { getCompanies } from "framework/company";
import { ICompany } from "models/company";

interface Props {
  feature: Feature;
}

function isValidNumber(countryCodeNumeric: string): boolean {
  const { isValid } = phone(countryCodeNumeric);
  return isValid;

}

const Users: React.FC<Props> = ({ feature }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [loading2, setLoading2] = useState<boolean>(false);
  const [loading4, setLoading4] = useState<boolean>(true);
  const [companies, setCompanies] = useState<ICompany[]>([])
  const [Users, setUsers] = useState<IUser[]>([] as IUser[]);
  const [Roles, setRoles] = useState<IRole[]>([] as IRole[]);
  const { t, i18n } = useTranslation();
  const [permission, setPermission] = useState<IPermission | null>(null);
  const [FilterUsers, setFilterUsers] = useState<IUser[]>([] as IUser[]);
  const user = getUserData();
  const { colors } = user;
  const [openAdd, setOpenAdd] = useState<boolean>(false);
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<IUser>({} as IUser);
  const [inputObj, setInputObj] = useState<IUserInput>({} as IUserInput);
  const { setAlert } = useAlert();

  // eslint-disable-next-line
  const queryClient = new QueryClient();

  const addUsersMutation = useMutation({
    mutationFn: (createInput: IUserInput) => {
      return addUser(createInput);
    },
  });

  const editUsersMutation = useMutation({
    mutationFn: (createInput: IUserInput) => {
      return editUser(createInput);
    },
  });

  const fetchRoles = useCallback(async () => {
    setLoading4(true);
    try {
      let response: any = await queryClient.fetchQuery("get Roles", () =>
        getRolesDropDown()
      );
      response = response = response && response.length > 0 ? response : [];
      setRoles(response);
      setLoading4(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading4(false);
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let response: any = await queryClient.fetchQuery("get Users", () =>
        getUsers()
      );

      setUsers(response);
      setFilterUsers(response);
      setLoading(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading(false);
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);



  const fetchCompanies = useCallback(async () => {
    try {
      let response: any = await queryClient.fetchQuery("get industries", () =>
        getCompanies()
      );
      setCompanies(response);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);

  useEffect(() => {
    fetchData();
    fetchRoles();
    fetchCompanies()
    // eslint-disable-next-line
  }, []);

  if (loading || loading4)
    return (
      <Stack spacing={2} p={2}>
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton variant="rectangular" key={i} width="100%" height={30} />
        ))}
      </Stack>
    );

  const handleChange = (name: string, value: any) => {
    setInputObj((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleOpenAdd = () => setOpenAdd(true);
  const handleCloseAdd = () => {
    setOpenAdd(false);
    setInputObj({} as IUserInput)
  }

  const handleOpenEdit = (User: IUser) => {
    setInputObj((prev) => ({
      ...prev,
      roleName: User.roleName,
      companyId: User.companyId
    }));
    setSelectedUser(User);
    setOpenEdit(true);
  };
  const handleCloseEdit = () => {
    setOpenEdit(false);
    setSelectedUser({} as IUser);
    setInputObj({} as IUserInput)
  };

  const getBodyCells = (row: IUser) => {
    let cells = Object.keys(row).map((key: string, i: number) => {
      if (key === "userName")
        return {
          id: "userName",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.userName}</Typography>
            </TableCell>
          ),
        };
      if (key === "email")
        return {
          id: "email",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.email}</Typography>
            </TableCell>
          ),
        };
      if (key === "countryCode")
        return {
          id: "countryCode",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.countryCode}</Typography>
            </TableCell>
          ),
        };
      if (key === "phoneNumber")
        return {
          id: "phoneNumber",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.phoneNumber}</Typography>
            </TableCell>
          ),
        };
      if (key === "roleName")
        return {
          id: "roleName",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.roleName}</Typography>
            </TableCell>
          ),
        };

      if (key === "companyId")
        return {
          id: "companyId",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row.roleName === 'Trainer' || row.roleName === 'SuperAdmin' ? row?.vendorName : companies.find(i => i?.id?.toLowerCase() === row?.companyId?.toLowerCase())?.name}</Typography>
            </TableCell>
          ),
        };

      if (key === "id")
        return {
          id: "actions",
          element: (
            <TableCell align={"left"} key={i}>
              <Stack
                direction={"row"}
                alignContent={"center"}
                spacing={1}
                justifyContent={i18n.language === "en" ? "start" : "end"}
              >
                <Tooltip title={t("T_edit")}>
                  <IconButton
                    size={"small"}
                    color={"primary"}
                    onClick={() => handleOpenEdit(row)}
                  >
                    <Edit />
                  </IconButton>
                </Tooltip>
              </Stack>
            </TableCell>
          ),
        };
      else return <></>;
    });
    return cells;
  };

  const handleAdd = async () => {
    if (
      !inputObj.userName ||
      !inputObj.email ||
      !inputObj.phoneNumber ||
      !inputObj.countryCode ||
      !inputObj.roleName
    )
      return setAlert({
        open: true,
        message: "Please fill all fields",
        type: "error",
      });

    if ((inputObj.roleName === "CompanyAdmin" || inputObj.roleName === "CorporateAdmin") && !inputObj.companyId)
      return setAlert({
        open: true,
        message: "Please Select Company",
        type: "error",
      });

    if (!isValidEmail(inputObj.email)) {
      return setAlert({
        open: true,
        message: "Please Enter valid email",
        type: "error",
      });
    }
    if (
      !isValidNumber(inputObj.countryCode + inputObj.phoneNumber))


      return setAlert({
        open: true,
        message: "This Phone is not valid in this country",
        type: "error",
      });

    try {
      setLoading2(true);

      await addUsersMutation.mutateAsync(inputObj);
      setAlert({
        open: true,
        message: "User added successfully",
        type: "success",
      });
      fetchData();
      handleCloseAdd();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleEdit = async () => {
    if (
      !inputObj.userName &&
      !inputObj.email &&
      !inputObj.phoneNumber &&
      !inputObj.countryCode &&
      !inputObj.roleName
    )
      return setAlert({
        open: true,
        message: "No changes detected",
        type: "error",
      });

    if ((inputObj.roleName === "CompanyAdmin" || inputObj.roleName === "CorporateAdmin") && !inputObj.companyId)
      return setAlert({
        open: true,
        message: "Please Select Company",
        type: "error",
      });

    if (!isValidEmail(inputObj.email || selectedUser.email)) {
      return setAlert({
        open: true,
        message: "Please Enter valid email",
        type: "error",
      });
    }

    if (
      !isValidNumber((inputObj.countryCode || selectedUser.countryCode) + (inputObj.phoneNumber || selectedUser.phoneNumber)))


      return setAlert({
        open: true,
        message: "This Phone is not valid in this country",
        type: "error",
      });

    try {
      setLoading2(true);
      let data: IUserInput = {
        id: selectedUser.id,
        userName: inputObj.userName || selectedUser.userName,
        email: inputObj.email || selectedUser.email,
        phoneNumber: inputObj.phoneNumber || selectedUser.phoneNumber,
        countryCode: inputObj.countryCode || selectedUser.countryCode,
        roleName: inputObj?.roleName,
        companyId: inputObj?.companyId
      };
      await editUsersMutation.mutateAsync(data);
      setAlert({
        open: true,
        message: "User updated successfully",
        type: "success",
      });
      fetchData();
      handleCloseEdit();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleSearch = async (value: string) => {
    if (!value) return setFilterUsers(Users);

    const search = Users.filter((item) =>
      (item.userName + " " + item.email + " " + item.phoneNumber + " " + item.countryCode + " " + item.roleName + " " + item?.companyName)
        .toLowerCase()
        .includes(value.toLowerCase())
    );
    setFilterUsers(search);
  };

  return (
    <>
      <Stack spacing={2} p={2}>
        <Grid
          container
          justifyContent={"space-between"}
          alignItems="center"
          sx={{ mb: 4 }}
        >
          <Typography
            variant="h3"
            color={colors?.labelColor ? colors?.labelColor : "header.primary"}
          >
            {t(feature.name)}
          </Typography>
          <Stack direction={"row"} spacing={1}>
            <Search handleChange={handleSearch} />
            <Tooltip title="Add User">
              <IconButton
                sx={{
                  color: colors.addUpdateIconColor
                    ? colors.addUpdateIconColor
                    : "red",
                  "&:hover": { color: colors?.addUpdateIconHoverColor },
                  height: 40,
                  width: 40,
                  transition: "0.5s",
                }}
                onClick={handleOpenAdd}
              >
                <Add />
              </IconButton>
            </Tooltip>
          </Stack>
        </Grid>

        {FilterUsers.length > 0 && (
          <List
            headCells={UserCells}
            data={FilterUsers}
            getBodyCells={getBodyCells}
            feature={feature}
            canEdit={false}
          />
        )}
        {(!FilterUsers || FilterUsers.length === 0) && (
          <EmptyContent
            icon={<BlindsClosedIcon sx={{ fontSize: "3.5rem " }} />}
            header={t("T_no_user_header")}
            message={t("T_no_user_message")}
          />
        )}
      </Stack>

      {openAdd && (
        <AddUser
          roles={Roles}
          input={inputObj}
          open={openAdd}
          loading={loading2}
          companies={companies}
          onClose={handleCloseAdd}
          onSubmit={handleAdd}
          onChanges={handleChange}
        />
      )}
      {openEdit && (
        <EditUser
          open={openEdit}
          roles={Roles}
          loading={loading2}
          User={selectedUser}
          companies={companies}
          input={inputObj}
          onClose={handleCloseEdit}
          onSubmit={handleEdit}
          onChanges={handleChange}
        />
      )}
    </>
  );
};

export default Users;
